import { ReactNode, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeHref } from '@/lib/marketing/localizedLandings';
import { Link } from 'wouter';
import { MarketingNav } from '@/components/MarketingNav';

const CookieSettingsButton = lazy(() =>
  import('@/components/CookieConsentBanner')
    .then(m => ({ default: m.CookieSettingsButton }))
    .catch(() => ({ default: () => null }))
);

interface LandingLayoutProps {
  children: ReactNode;
  /**
   * Accepted for API compatibility. The new shell uses a single, consistent
   * navbar style — the prop is now a no-op.
   */
  transparentHeader?: boolean;
}

/**
 * LandingLayout — marketing shell for non-app pages (homepage variants,
 * vertical landings, about / features / pricing / legal).
 *
 * The navbar (shared MarketingNav) and footer give all marketing pages
 * one identity. Inside `<main>` we re-map common slate/gray Tailwind
 * text utilities to the new foreground grey, and apply Lexend to headings.
 */
export function LandingLayout({ children }: LandingLayoutProps) {
  const { t, i18n } = useTranslation('footer');

  return (
    <div className="ml-shell">
      <style>{shellStyles}</style>
      <MarketingNav />
      <main className="ml-main">{children}</main>

      <footer className="ml-footer">
        <div>
          <div className="brand" style={{ marginBottom: 14 }}><img src="/images/logo_horizontal_transparent.png" alt="Sloty" className="brand-logo footer-logo" /></div>
          <p className="tagline">{t('tagline')}</p>
        </div>
        <div>
          <h5>{t('product')}</h5>
          <ul>
            <li><Link href="/features">{t('features')}</Link></li>
            <li><Link href="/pricing">{t('pricing')}</Link></li>
            <li><Link href="/about">{t('about')}</Link></li>
            <li><Link href="/blog">{t('blog')}</Link></li>
          </ul>
        </div>
        <div>
          <h5>{t('solutions')}</h5>
          <ul>
            <li><Link href={localizeHref('/for-salons', i18n.language)}>{t('forSalons')}</Link></li>
            <li><Link href={localizeHref('/for-clinics', i18n.language)}>{t('forClinics')}</Link></li>
            <li><Link href={localizeHref('/for-trainers', i18n.language)}>{t('forTrainers')}</Link></li>
            <li><Link href={localizeHref('/for-small-gyms', i18n.language)}>{t('forSmallGyms')}</Link></li>
            <li><Link href={localizeHref('/for-therapists', i18n.language)}>{t('forTherapists')}</Link></li>
            <li><Link href={localizeHref('/for-coaches', i18n.language)}>{t('forCoaches')}</Link></li>
          </ul>
        </div>
        <div>
          <h5>{t('legal')}</h5>
          <ul>
            <li><Link href="/privacy">{t('privacy')}</Link></li>
            <li><Link href="/terms">{t('terms')}</Link></li>
            <li><Link href="/cookies">{t('cookies')}</Link></li>
            <li><Link href="/impressum">{t('impressum')}</Link></li>
            <li><Suspense fallback={null}><CookieSettingsButton /></Suspense></li>
          </ul>
        </div>
        <div className="copyright">© {new Date().getFullYear()} Sloty.</div>
      </footer>
    </div>
  );
}

const shellStyles = `
.ml-shell{
  --ml-background: 210 20% 98%;
  --ml-foreground: 215 25% 27%;
  --ml-muted: 220 14% 96%;
  --ml-muted-foreground: 215 16% 35%;
  --ml-border: 220 13% 91%;
  --ml-action-primary: 217 91% 53%;
  --ml-action-primary-hover: 224 76% 42%;
  --ml-action-primary-light: 217 91% 95%;
  --ml-brand-500:#2563EB;
  --ml-brand-700:#1E40AF;
  background:hsl(var(--ml-background));
  color:hsl(var(--ml-foreground));
  font-family:Inter,system-ui,sans-serif;
  -webkit-font-smoothing:antialiased;
  min-height:100vh;
  display:flex;
  flex-direction:column;
}
.ml-shell *{box-sizing:border-box;}
.ml-shell h1,.ml-shell h2,.ml-shell h3,.ml-shell h4,.ml-shell h5,.ml-shell h6{font-family:Lexend,system-ui,sans-serif;font-weight:600;letter-spacing:-0.01em;}
.ml-shell .ml-main{flex:1;}

/* Re-map slate/gray "near-black" tokens used by existing pages
 * to the new foreground grey so the brand palette stays consistent
 * without touching every page. Brand-* and other utilities pass through. */
.ml-shell .ml-main .text-slate-900,
.ml-shell .ml-main .text-gray-900{color:hsl(var(--ml-foreground)) !important;}
.ml-shell .ml-main .text-slate-600,
.ml-shell .ml-main .text-gray-600,
.ml-shell .ml-main .text-slate-500,
.ml-shell .ml-main .text-gray-500{color:hsl(var(--ml-muted-foreground)) !important;}
.ml-shell .ml-main .bg-white{background-color:#fff;}
.ml-shell .ml-main .bg-slate-50,
.ml-shell .ml-main .bg-gray-50{background-color:hsl(var(--ml-muted)) !important;}
.ml-shell .ml-main .border-slate-200,
.ml-shell .ml-main .border-gray-200{border-color:hsl(var(--ml-border)) !important;}

/* Nav */
.ml-shell .ml-nav{display:flex;justify-content:space-between;align-items:center;padding:22px 32px;max-width:1320px;margin:0 auto;width:100%;position:relative;}
.ml-shell .ml-nav a,.ml-shell .ml-footer a,.ml-shell .mobile-menu a{color:inherit;text-decoration:none;cursor:pointer;}
.ml-shell .brand{font-family:Lexend;font-weight:700;font-size:22px;letter-spacing:-0.02em;display:inline-flex;align-items:center;gap:10px;color:hsl(var(--ml-foreground));}
.ml-shell .brand-logo{height:40px;width:auto;display:block;}
.ml-shell .footer-logo{height:32px;}
.ml-shell .ml-nav .links{display:flex;gap:24px;align-items:center;font-size:14px;color:hsl(var(--ml-muted-foreground));}
.ml-shell .ml-nav .links a:hover,.ml-shell .ml-nav .solutions-trigger:hover{color:hsl(var(--ml-foreground));}
.ml-shell .ml-nav .cta{background:hsl(var(--ml-action-primary));color:#fff;padding:10px 18px;border-radius:9999px;font-size:14px;font-weight:500;min-height:44px;display:inline-flex;align-items:center;}
.ml-shell .ml-nav .cta:hover{background:hsl(var(--ml-action-primary-hover));color:#fff;}
.ml-shell .solutions{position:relative;}
.ml-shell .solutions-trigger{background:none;border:0;padding:0;font:inherit;color:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:4px;font-size:14px;}
.ml-shell .solutions-trigger .chev{width:14px;height:14px;}
.ml-shell .solutions-panel{position:absolute;top:100%;left:50%;transform:translateX(-50%);padding-top:10px;min-width:560px;z-index:60;}
.ml-shell .solutions-panel > .sol-grid{background:#fff;border:1px solid hsl(var(--ml-border));border-radius:14px;padding:20px;display:grid;grid-template-columns:repeat(3,1fr);gap:24px;box-shadow:0 14px 40px rgba(15,23,42,0.10);}
.ml-shell .solutions-panel .sol-h{font-size:11px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--ml-muted-foreground));margin-bottom:10px;}
.ml-shell .solutions-panel ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:4px;}
.ml-shell .solutions-panel li a{display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:8px;font-size:14px;color:hsl(var(--ml-foreground));}
.ml-shell .solutions-panel li a:hover{background:hsl(var(--ml-action-primary-light));color:var(--ml-brand-700);}
.ml-shell .sol-ic{width:16px;height:16px;color:var(--ml-brand-500);}
.ml-shell .mobile-actions{display:none;align-items:center;gap:8px;}
.ml-shell .mobile-toggle{background:none;border:0;padding:8px;cursor:pointer;color:hsl(var(--ml-foreground));}
.ml-shell .mobile-toggle .ic{width:24px;height:24px;}
.ml-shell .mobile-only{display:none;}
.ml-shell .desktop-only{display:flex;}
.ml-shell .mobile-menu{flex-direction:column;gap:4px;padding:12px 24px 20px;border-bottom:1px solid hsl(var(--ml-border));background:hsl(var(--ml-background));}
.ml-shell .mobile-menu a,.ml-shell .mobile-menu .m-solutions-trigger{padding:12px 8px;font-size:15px;color:hsl(var(--ml-foreground));border-radius:8px;background:none;border:0;text-align:left;font-family:inherit;display:flex;align-items:center;justify-content:space-between;cursor:pointer;}
.ml-shell .mobile-menu .cta{background:hsl(var(--ml-action-primary));color:#fff;justify-content:center;margin-top:6px;}
.ml-shell .mobile-menu .m-solutions-trigger .chev{width:16px;height:16px;transition:transform .2s;}
.ml-shell .mobile-menu .m-solutions-trigger .chev.open{transform:rotate(180deg);}
.ml-shell .m-solutions{display:flex;flex-direction:column;gap:14px;padding:6px 0 6px 12px;border-left:2px solid hsl(var(--ml-border));margin-left:8px;}
.ml-shell .m-solutions .sol-h{font-size:11px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:hsl(var(--ml-muted-foreground));margin-bottom:4px;}
.ml-shell .m-solutions a{padding:8px;font-size:14px;}

/* Footer */
.ml-shell .ml-footer{border-top:1px solid hsl(var(--ml-border));padding:48px 32px;display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:32px;max-width:1320px;margin:0 auto;width:100%;}
.ml-shell .ml-footer h5{font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:hsl(var(--ml-muted-foreground));margin:0 0 14px;}
.ml-shell .ml-footer ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px;font-size:14px;color:hsl(var(--ml-muted-foreground));}
.ml-shell .ml-footer ul a:hover{color:hsl(var(--ml-foreground));}
.ml-shell .ml-footer .tagline{font-size:13px;color:hsl(var(--ml-muted-foreground));max-width:240px;margin:0;}
.ml-shell .ml-footer .copyright{grid-column:1 / -1;font-size:12px;color:hsl(var(--ml-muted-foreground));margin-top:8px;}

@media (max-width:980px){
  .ml-shell .desktop-only{display:none;}
  .ml-shell .mobile-only{display:flex;}
  .ml-shell .mobile-actions{display:flex;}
  .ml-shell .ml-footer{grid-template-columns:1fr 1fr;}
}
`;
